import React, { useEffect, useState } from "react";
import { BarLoader } from "react-spinners";

interface IProps{
  columns: {
    name: string;
    sortable?: boolean;
    field: string;
    searchable?: boolean;
    render?: (cell: any, row: any)=> React.ReactNode
  }[];
  data: any;
  loading?: boolean;
  totalPage: number;
  page: {of: number; size: number};
  onPage: (value:{of:number; size: number})=>void;
  sort: {by: string; order: 'desc' | 'asc'}[];
  onSort: (value:{by: string; order: 'desc' | 'asc'}[])=> void;
  filter: any;
  onFilter: (value: any)=> void;
  check?: any;
  onCheckChange?: (value?: null | any)=>void;
  expandComponent?: ((value: any)=> React.ReactNode) | null;
}

const TableV3 = ({
  columns,
  data,
  loading=false,
  totalPage,
  page,
  onPage,
  sort = [],
  onSort,
  filter,
  onFilter,
  check = null,
  onCheckChange,
  expandComponent = null,
}:IProps) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [filterState, setFilterState] = useState<any>({});
  const [openedEpandComponent, setOpenedExpandComponent] = useState<number[]>([]);

  const isAllSelected =
    selectedItems.length > 0 &&
    data.length > 0 &&
    selectedItems.length === data.length;

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedItems((prevSelected) => {
      if (isChecked) {
        onCheckChange?.([...prevSelected, id]);
        return [...prevSelected, id];
      } else {
        onCheckChange?.([...prevSelected.filter((item) => item !== id)]);
        return prevSelected.filter((item) => item !== id);
      }
    });
  };

  const handleHeaderCheckboxChange = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = data.map((_: any, index: any) => index); // Get all row IDs dynamically from data
      setSelectedItems(allIds); // Select all items
      onCheckChange?.(allIds);
    } else {
      setSelectedItems([]); // Deselect all items
      onCheckChange?.([]);
    }
  };
  const nestedField = (row: { [x: string]: any; }, field: string) => {
    const splited = field.split(".");
    if (splited.length > 1) {
      let dt = row[splited[0]];

      for (let i = 1; i < splited.length; i++) {
        if (!!dt && dt[splited[i]]) {
          dt = dt[splited[i]];
        } else {
          dt = null;
          i += splited.length;
        }
      }

      return dt;
    } else {
      return row[field];
    }
  };

  const listPage = (total: number) => {
    let list: any[] = [];
    for (let i = 1; i <= total; i++) {
      list = [...list, i];
    }
    return list;
  };

  const handleSort = (item: { field?: any; }, sortItem: { by: string; order: "desc" | "asc"; } | undefined) => {
    if (!!item.field) {
      let newData: {
        by: string;
        order: 'asc' | 'desc'
      } | null = null;
      if (!!!sortItem) {
        newData = {
          by: item.field,
          order: "asc",
        };
      }
      if (!!sortItem && sortItem.order === "asc") {
        newData = {
          by: item.field,
          order: "desc",
        };
      }
      if (!!newData) {
        onSort?.([...sort.filter((sitem) => sitem.by !== item.field), newData]);
      } else {
        onSort?.([...sort.filter((sitem) => sitem.by !== item.field)]);
      }
    }
  };

  useEffect(() => {
    setFilterState({ ...filter });
  }, [filter]);

  useEffect(() => {
    if (!!check) {
      setSelectedItems(check);
    }
  }, [check]);
  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              {!!check ? (
                <th>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        handleHeaderCheckboxChange(e.currentTarget.checked)
                      }
                      checked={isAllSelected}
                    />
                  </label>
                </th>
              ) : null}
              {!!expandComponent ? <th></th> : null}
              {columns.map((item, key) => {
                const sortItem = sort.find((s) => s.by === item.field);
                return (
                  <th key={key}>
                    {item.name}{" "}
                    {item.sortable ? (
                      <a
                        href="/"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSort(item, sortItem);
                        }}
                      >
                        {!!sortItem ? (
                          sortItem.order === "desc" ? (
                            <i className="fa fa-sort-asc"></i>
                          ) : (
                            <i className="fa fa-sort-desc"></i>
                          )
                        ) : (
                          <i className="fa fa-sort"></i>
                        )}
                      </a>
                    ) : null}
                    {item.searchable ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();

                          onFilter?.({ ...filterState });
                        }}
                      >
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder={item.name}
                          name={item.field}
                          value={
                            !!item.field ? filterState[item.field] ?? "" : ""
                          }
                          onChange={(e) => {
                            const newData = {
                              ...filterState,
                              [e.currentTarget.name]: e.currentTarget.value,
                            };
                            if (newData[e.currentTarget.name] === "") {
                              delete newData[e.currentTarget.name];
                            }
                            setFilterState({
                              ...newData,
                            });
                          }}
                        />
                      </form>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (!!check ? 1 : 0) +
                    (!!expandComponent ? 1 : 0)
                  }
                >
                  <center>
                    <BarLoader color={"#123abc"} loading /> <br /> Loading.... Please
                    wait...
                  </center>
                </td>
              </tr>
            ) : (
              <>
                {data.length > 0 ? (
                  <>
                    {data.map((item: any, key: React.Key | null | undefined) => (
                      <React.Fragment key={key}>
                        <tr>
                          {!!check ? (
                            <td>
                              <label className="checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.includes(key)}
                                  onChange={(e) =>
                                    handleCheckboxChange(
                                      Number(key),
                                      e.currentTarget.checked
                                    )
                                  }
                                />
                              </label>
                            </td>
                          ) : null}
                          {!!expandComponent ? (
                            <td>
                              <a
                                href="/"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (openedEpandComponent.findIndex(
                                      (item) => item === key
                                    )>=0
                                  ) {
                                    setOpenedExpandComponent([
                                      ...openedEpandComponent.filter(
                                        (item) => item !== key
                                      ),
                                    ]);
                                  } else {
                                    setOpenedExpandComponent([
                                      ...openedEpandComponent,
                                      Number(key),
                                    ]);
                                  }
                                }}
                              >
                                {openedEpandComponent.findIndex((item)=>item===key)>=0 ? <i className="fa fa-chevron-down"></i>:<i className="fa fa-chevron-right"></i>}
                              </a>
                            </td>
                          ) : null}
                          {columns.map((column, key) => (
                            <td key={key}>
                              {!!column.render
                                ? column.render(
                                    !!column.field
                                      ? nestedField(item, column.field)
                                      : null,
                                    item
                                  )
                                : !!column.field
                                ? nestedField(item, column.field)
                                : null}
                            </td>
                          ))}
                        </tr>
                        {!!expandComponent && !!expandComponent(item) && openedEpandComponent.findIndex((item)=>item===key)>=0 ? (
                          <tr>
                            <td
                              colSpan={
                                columns.length +
                                (!!check ? 1 : 0) +
                                (!!expandComponent ? 1 : 0)
                              }
                            >
                              {expandComponent(item)}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={
                        columns.length +
                        (!!check ? 1 : 0) +
                        (!!expandComponent ? 1 : 0)
                      }
                    >
                      No data
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      <nav aria-label="Page navigation example">
        <ul className="pagination">
          <li className="page-item">
            <a
              className={`page-link ${page.of === 1 ? `disabled` : ``}`}
              href="/"
              aria-label="Previous"
              onClick={(e) => {
                e.preventDefault();

                if (page.of > 1) {
                  onPage({ ...page, of: page.of - 1 });
                  handleHeaderCheckboxChange(false);
                }
              }}
            >
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
          {page.of > 3 ? (
            <li className={`page-item ${1 === page.of ? `active` : ``}`}>
              <a
                href="/"
                className={`page-link  `}
                aria-label={`Goto page 1`}
                onClick={(e) => {
                  e.preventDefault();
                  handleHeaderCheckboxChange(false);
                  return onPage({ ...page, of: 1 });
                }}
              >
                1
              </a>
            </li>
          ) : null}
          {page.of > 3 ? (
            <li className={`page-item`}>
              <a
                href="/"
                className={`page-link`}
                onClick={(e) => {
                  e.preventDefault();
                  return null;
                }}
              >
                ...
              </a>
            </li>
          ) : null}
          {listPage(totalPage)
            .filter(
              (item) =>
                page.of -
                  (totalPage - page.of <= 2 ? 5 - (totalPage - page.of) : 3) <
                  item && item <= page.of + (page.of <= 2 ? 5 - page.of : 2)
            )
            .map((item, key) => (
              <li
                key={key}
                className={`page-item ${item === page.of ? `active` : ``}`}
              >
                <a
                  href="/"
                  className={`page-link  `}
                  aria-label={`Goto page ${item}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleHeaderCheckboxChange(false);
                    return onPage({ ...page, of: item });
                  }}
                >
                  {item}
                </a>
              </li>
            ))}

          {page.of <= totalPage - 3 ? (
            <li className={`page-item`}>
              <a
                href="/"
                className={`page-link`}
                onClick={(e) => {
                  e.preventDefault();
                  return null;
                }}
              >
                ...
              </a>
            </li>
          ) : null}

          {page.of <= totalPage - 3 ? (
            <li
              className={`page-item ${totalPage === page.of ? `active` : ``}`}
            >
              <a
                href="/"
                className={`page-link  `}
                aria-label={`Goto page ${totalPage}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleHeaderCheckboxChange(false);
                  return onPage({ ...page, of: totalPage });
                }}
              >
                {totalPage}
              </a>
            </li>
          ) : null}
          <li className="page-item">
            <a
              className={`page-link ${page.of === totalPage ? `disabled` : ``}`}
              href="/"
              aria-label="Next"
              onClick={(e) => {
                e.preventDefault();

                if (page.of < totalPage) {
                  onPage({ ...page, of: page.of + 1 });
                  handleHeaderCheckboxChange(false);
                }
              }}
            >
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default TableV3;
